import { Box, Grid, Typography } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { CustomizedBox } from "../../../components/Custom/CustomizedBox";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import AgGrid from "../../../components/Table/AgGrid";
import TotalBox from "../../../components/UI/TotalBox";
import {
  getItemsCurrentStockLocation,
  getItemsCurrentStockSerialNumber,
} from "../../../features/Inventory/Report/report-actions";
import { filterParamsOptions } from "../../../utils/filterparams";
import { formatExportQuotes } from "../../../utils/exporter";
import InventoryService from "../../../services/Inventory";
import { getAllLocation } from "../../../features/Setting/Location/location-actions";
import { formatNumber } from "../../../utils/dataTransformer";
import { useSnackbar } from "notistack";

const OnHandItemWarehouse = () => {
  const onHandItemByWarehouseGridRef = useRef();
  const onHandItemByLocationGridRef = useRef();
  const onHandItemBySNGridRef = useRef();
  const csvRef = useRef(null);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [exportData, setExportData] = useState([]);
  const { t } = useTranslation();
  const { item } = useSelector((state) => state.itemMaster);
  const dispatch = useDispatch();
  const [newData, setNewData] = useState([]);
  const { allLocation } = useSelector((state) => state.location);
  const { id } = useParams();

  const { enqueueSnackbar } = useSnackbar();

  // const columnDefsWarehouse = useMemo(() => [], []);

  const datasource = {
    getRows(params) {
      const request = params.request;
      const filterItemCurrentStock = {
        ...request.filterModel,
        item_document_id: {
          filterType: "text",
          type: "equals",
          filter: id,
          mode: "default",
        },
      };
      dispatch(
        getItemsCurrentStockLocation(
          {
            startRow: request.startRow,
            endRow: request.endRow,
            filterModel: filterItemCurrentStock,
            sortModel: request.sortModel,
          },
          params,
          enqueueSnackbar,
          "new",
        ),
      );
    },
  };

  const datasourceSN = {
    getRows(params) {
      const request = params.request;
      const filterItemCurrentStock = {
        ...request.filterModel,
        item_document_id: {
          filterType: "text",
          type: "equals",
          filter: id,
          mode: "default",
        },
      };
      dispatch(
        getItemsCurrentStockSerialNumber(
          {
            startRow: request.startRow,
            endRow: request.endRow,
            filterModel: filterItemCurrentStock,
            sortModel: request.sortModel,
          },
          params,
          enqueueSnackbar,
        ),
      );
    },
  };

  const changePayloadData = useCallback(
    (allData) => {
      const warehouseIdToDocumentId = { 2: "WH01", 3: "WH02" };
      let itemData = [];
      let checkArray = [];
      allData.forEach((item) => {
        if (!checkArray.includes(item.item_document_id)) {
          checkArray.push(item.item_document_id);
          itemData.push({ item_document_id: item.item_document_id });
        }
        let findItem = itemData.find(
          (x) => x.item_document_id === item.item_document_id,
        );
        const documentId = warehouseIdToDocumentId[item.warehouse_id];
        const warehouse = allLocation?.find((w) => w.document_id === documentId);
        if (!warehouse) return;
        let myValue = item["current_quantity"];
        if (typeof myValue === "number") myValue = parseFloat(myValue);
        findItem[warehouse.thai_name] = myValue;
      });
      setNewData(itemData);
    },
    [allLocation],
  );

  const findInput = useMemo(() => {
    return {
      findManyInput: {},
    };
  }, []);

  useEffect(() => {
    dispatch(getAllLocation(findInput));
  }, [dispatch, findInput]);

  const getData = useCallback(async () => {
    const input = {
      startRow: 0,
      endRow: 9999999,
      filterModel: {
        item_document_id: {
          filterType: "text",
          type: "equals",
          filter: id,
          mode: "default",
        },
      },
    };
    const { results } = await InventoryService.getOnHandICSByWarehouse(input);
    changePayloadData(results);
  }, [changePayloadData, id]);

  useEffect(() => {
    getData();
  }, [getData]);

  const onGridReady = (params) => {
    params.api.setServerSideDatasource(datasource);
  };

  const onGridReadySN = (params) => {
    params.api.setServerSideDatasource(datasourceSN);
  };

  const csvHeaders = [
    { label: "หมวดหมู่สินค้า 1", key: "item_group_sub_level_1_name" },
    { label: "รหัสสินค้า", key: "item_document_id" },
    { label: "ชื่อสินค้า", key: "item_name" },
    { label: "รายละเอียดสินค้า", key: "item_description" },
    { label: "จำนวนคงคลัง", key: "current_quantity" },
    { label: "SN", key: "serial_number" },
    { label: "Barcode", key: "batch_number" },
    { label: "คลัง", key: "warehouse_thai_name" },
    { label: "สถานที่", key: "bin_location_name" },
  ];

  const exportSNHandler = async () => {
    try {
      setIsLoadingExport(true);
      const filterModel =
        onHandItemBySNGridRef.current?.api?.getFilterModel() || {};
      const filterItemCurrentStock = {
        ...filterModel,
        item_document_id: {
          filterType: "text",
          type: "equals",
          filter: id,
          mode: "default",
        },
      };
      const exportInput = {
        startRow: 0,
        endRow: Math.pow(10, 5),
        filterModel: filterItemCurrentStock,
      };
      const { results } = await InventoryService.getOnHandICSBySN(exportInput);
      setExportData(formatExportQuotes(results));
      setTimeout(() => {
        if (csvRef.current) {
          csvRef.current.link.click();
        }
      });
      enqueueSnackbar("นำออกข้อมูลสำเร็จ", { variant: "success" });
    } catch (error) {
      console.error("Error exporting CSV:", error);
      enqueueSnackbar("นำออกข้อมูลไม่สำเร็จ", { variant: "error" });
    } finally {
      setIsLoadingExport(false);
    }
  };

  const columnDefsWarehouse =
    allLocation &&
    allLocation.map((location) => {
      return {
        field: location.thai_name,
        headerName: location.thai_name,
        filter: false,
        floatingFilter: false,
        sortable: false,
        valueFormatter: (params) =>
          params.value == null
            ? ""
            : Number(params.value).toLocaleString("en-US", {
                maximumFractionDigits: 0,
              }),
      };
    });

  const columnDefsLocation = [
    {
      field: "warehouse_document_id",
      headerName: t("inventory.warehouseId"),
      filter: "agTextColumnFilter",
    },
    {
      field: "warehouse_thai_name",
      headerName: t("inventory.warehouse"),
      filter: "agTextColumnFilter",
    },
    {
      field: "bin_location_document_id",
      headerName: t("inventory.itemCurrentStockLocation.binLocation"),
      filter: "agTextColumnFilter",
    },
    // {
    //   field: "bin_location_name",
    //   headerName: t("inventory.itemCurrentStockLocation.binLocation"),
    //   filter: "agTextColumnFilter",
    // },
    {
      field: "current_quantity",
      headerName: t("inventory.itemCurrentStock.currentQuantity"),
      filter: "agNumberColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("number"),
      },
      valueFormatter: (params) => formatNumber(params.value),
    },
    {
      field: "current_value",
      headerName: t("inventory.itemCurrentStock.currentValue"),
      filter: "agNumberColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("number"),
      },
      hide: true,
    },
  ];

  const columnDefsLocationSN = [
    {
      field: "warehouse_document_id",
      headerName: t("inventory.warehouseId"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "warehouse_thai_name",
      headerName: t("inventory.warehouse"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "serial_number",
      headerName: t("inventory.serialNumber"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "bin_location_document_id",
      headerName: t("inventory.itemCurrentStockLocation.binLocation"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "current_quantity",
      headerName: t("inventory.itemCurrentStock.currentQuantity"),
      filter: "agNumberColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("number"),
      },
      valueFormatter: (params) => formatNumber(params.value),
    },
    {
      field: "batch_number",
      headerName: "Barcode",
      width: 350,
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
  ];

  const firstCardList = [
    {
      title: t("inventory.quantities.currentAvailableQty"),
      field: "total",
      value: formatNumber(item.current_available_qty),
    },
    {
      title: t("inventory.quantities.currentStockQty"),
      field: "current_stock_qty",
      value: formatNumber(item.current_stock_qty),
    },
  ];

  const secoundCardList = [
    {
      title: t("inventory.quantities.currentOrderedPurchaseQty"),
      field: "current_ordered_purchase_qty",
      value: formatNumber(item.current_ordered_purchase_qty),
      borderColor: "#328F35",
    },
    {
      title: t("inventory.quantities.currentOrderedManufactureQty"),
      field: "current_ordered_manufacture_qty",
      value: formatNumber(item.current_ordered_manufacture_qty),
      borderColor: "#328F35",
    },
    {
      title: t("inventory.quantities.currentCommittedSalesQty"),
      field: "current_committed_sales_qty",
      value: formatNumber(item.current_committed_sales_qty),
      borderColor: "#328F35",
    },
    {
      title: t("inventory.quantities.currentCommittedManufactureQty"),
      field: "current_committed_manufacture_qty",
      value: formatNumber(item.current_committed_manufacture_qty),
      borderColor: "#328F35",
    },
  ];

  const renderCard = (list) =>
    list.map((menu) => (
      <Grid item xs={12} sm={6} md={3} lg={3} xl={2} key={menu.title}>
        <TotalBox
          title={menu.title}
          total={menu.value}
          unit={item.base_uom.name}
          borderColor={menu.borderColor}
        />
      </Grid>
    ));

  return (
    <>
      <Grid container spacing={1}>
        {renderCard(firstCardList)}
      </Grid>
      <Grid container spacing={1}>
        {renderCard(secoundCardList)}
      </Grid>
      <CustomizedBox>
        <Typography fontWeight={"bold"} mb={3}>
          {t("inventory.items.onHandItemWarehouseTab")}
        </Typography>
        <AgGrid
          ref={onHandItemByWarehouseGridRef}
          columnDefs={columnDefsWarehouse}
          rowData={newData}
          // onGridReady={onGridReadyST}
          height={450}
          disabledSidebar
        />
      </CustomizedBox>
      <CustomizedBox>
        <Typography fontWeight={"bold"} mb={3}>
          {t("inventory.items.onHandItemWarehouse")}
        </Typography>
        <AgGrid
          ref={onHandItemByLocationGridRef}
          columnDefs={columnDefsLocation}
          onGridReady={onGridReady}
          height={450}
          disabledSidebar
        />
      </CustomizedBox>
      <CustomizedBox>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography fontWeight={"bold"}>
            {t("inventory.items.onHandItemWarehouseSN")}
          </Typography>
          <Box>
            <CustomizedButton
              title="นำออกข้อมูล"
              variant="contained"
              onClick={exportSNHandler}
              disabled={isLoadingExport}
            />
            <CSVLink
              headers={csvHeaders}
              data={exportData}
              filename={`${t("inventory.itemCurrentStockSerialNumber.index")}.csv`}
              ref={csvRef}
            />
          </Box>
        </Box>
        <AgGrid
          ref={onHandItemBySNGridRef}
          columnDefs={columnDefsLocationSN}
          onGridReady={onGridReadySN}
          height={450}
          disabledSidebar
        />
      </CustomizedBox>
    </>
  );
};

export default OnHandItemWarehouse;
